import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getCartCount, clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const provider = (searchParams.get('provider') || 'paytr').toLowerCase();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [iframeUrl, setIframeUrl] = useState('');
  const [iyzicoHtml, setIyzicoHtml] = useState('');
  const [order, setOrder] = useState(null);
  const iyzicoMountRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        // Try to fetch order info for the right-side summary (best-effort)
        try {
          const lastEmail = sessionStorage.getItem('lastOrderEmail') || '';
          const orderRes = await fetch(
            `${API_URL}/api/orders/${encodeURIComponent(orderId)}` +
            (lastEmail ? `?email=${encodeURIComponent(lastEmail)}` : '')
          );
          if (orderRes.ok) setOrder(await orderRes.json());
        } catch (_) {}

        // Provider-specific init
        const endpoint = provider === 'iyzico'
          ? `${API_URL}/api/payment/iyzico/init`
          : `${API_URL}/api/payment/get-token`;

        const tokRes = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_id: orderId }),
        });
        const tokData = await tokRes.json();
        if (!tokRes.ok) {
          setError(tokData.detail || 'Ödeme başlatılamadı');
          return;
        }

        if (provider === 'iyzico') {
          if (tokData.checkoutFormContent) {
            setIyzicoHtml(tokData.checkoutFormContent);
          } else if (tokData.paymentPageUrl) {
            // Fallback: redirect to Iyzico hosted page
            window.location.href = tokData.paymentPageUrl;
          } else {
            setError('Iyzico ödeme formu yüklenemedi');
          }
        } else {
          setIframeUrl(tokData.iframe_url);
        }

        try { clearCart(); } catch (_) {}
      } catch (e) {
        setError('Bağlantı hatası: ' + e.message);
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, provider]);

  // After Iyzico HTML is in the DOM, manually execute its <script> tags so the
  // Iyzico iframe actually mounts (React's dangerouslySetInnerHTML doesn't run scripts).
  useEffect(() => {
    if (!iyzicoHtml || !iyzicoMountRef.current) return;
    const root = iyzicoMountRef.current;
    const scripts = Array.from(root.querySelectorAll('script'));
    scripts.forEach((oldScript) => {
      const s = document.createElement('script');
      // Copy attributes (src, type, etc.)
      for (const attr of oldScript.attributes) s.setAttribute(attr.name, attr.value);
      s.text = oldScript.text;
      oldScript.parentNode.replaceChild(s, oldScript);
    });
  }, [iyzicoHtml]);

  const isIyzico = provider === 'iyzico';
  const accentColor = isIyzico ? 'blue' : 'red';
  const providerName = isIyzico ? 'Iyzico' : 'PayTR';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header cartCount={getCartCount()} />

      <main className="max-w-6xl mx-auto px-4 py-10">
        <button
          onClick={() => navigate(-1)}
          data-testid="payment-back-btn"
          className={`inline-flex items-center gap-2 text-sm text-gray-600 hover:text-${accentColor}-600 mb-6`}
        >
          <ArrowLeft size={16} /> Geri
        </button>

        <h1 className="text-3xl font-bold text-gray-800 mb-2">Güvenli Ödeme</h1>
        <p className="text-gray-600 mb-8 flex items-center gap-2">
          <ShieldCheck size={18} className="text-emerald-600" />
          Ödemeniz <strong>{providerName}</strong> üzerinden güvenle gerçekleştirilir. Kart bilgileriniz sitemizde saklanmaz.
        </p>

        {loading && (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm">
            <Loader2 size={32} className={`mx-auto animate-spin text-${accentColor}-600 mb-3`} />
            <p className="text-gray-600">Ödeme sayfası hazırlanıyor...</p>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <p className="font-semibold text-red-700 mb-1">Ödeme başlatılamadı</p>
            <p className="text-sm text-red-600 mb-4">{error}</p>
            <Link to="/cart" className="inline-block px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
              Sepete Dön
            </Link>
          </div>
        )}

        {(iframeUrl || iyzicoHtml) && !loading && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm overflow-hidden">
              {isIyzico ? (
                <div
                  ref={iyzicoMountRef}
                  data-testid="iyzico-mount"
                  id="iyzipay-checkout-form"
                  className="responsive p-4 min-h-[700px]"
                  // checkoutFormContent is HTML+JS provided by Iyzico; we sanitize on the
                  // server side (it's only ever set from a successful Iyzico API call).
                  dangerouslySetInnerHTML={{ __html: iyzicoHtml }}
                />
              ) : (
                <iframe
                  src={iframeUrl}
                  title="PayTR Ödeme"
                  data-testid="paytr-iframe"
                  width="100%"
                  height="700"
                  frameBorder="0"
                  scrolling="auto"
                />
              )}
            </div>

            <aside className="bg-white rounded-xl p-5 shadow-sm h-fit">
              <h3 className="font-bold text-gray-800 mb-3">Sipariş Özeti</h3>
              <div className="text-sm text-gray-600 space-y-1 mb-4">
                <p><span className="text-gray-500">No:</span> <span className="font-mono">{orderId}</span></p>
                {order && order.total && (
                  <p><span className="text-gray-500">Toplam:</span> <strong className={`text-${accentColor}-600`}>{order.total.toFixed(2)} ₺</strong></p>
                )}
                <p><span className="text-gray-500">Sağlayıcı:</span> <strong>{providerName}</strong></p>
              </div>
              <div className="text-xs text-gray-500 space-y-2 border-t pt-3">
                <p>✓ Tüm büyük bankaların kartları kabul edilir</p>
                <p>✓ 3D Secure ile korunmuş işlem</p>
                <p>✓ Taksit seçenekleri ödeme formunda gösterilir</p>
              </div>
            </aside>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default PaymentPage;
